import React, { useState, useRef } from 'react';
import { Play, Pause, Clock, Video, ShoppingCart, Check, Lock, Crown, Sparkles, Trash2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useCart } from '@/context/CartContext';
import { useMembership } from '@/context/MembershipContext';
import { useAuth } from '@/context/AuthContext';
import { UpgradeModal } from '@/components/UpgradeModal';
import { PageEditable } from '@/components/edit/PageEditable';
import { usePageEditMode } from '@/context/PageEditModeContext';
import { toast } from 'sonner';

// Helper to detect if URL is Vimeo/YouTube
const getVideoEmbedUrl = (url) => {
  if (!url) return null;
  
  // Vimeo - use official embed parameters
  const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}?badge=0&autopause=0&player_id=0&app_id=58479`;
  }
  
  // YouTube
  const youtubeMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
  if (youtubeMatch) {
    return `https://www.youtube.com/embed/${youtubeMatch[1]}?autoplay=1&mute=1&controls=0`;
  }
  
  return null; // Direct video file
};

export const VideoCourseCard = ({ course, onDelete }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showTrailer, setShowTrailer] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const videoRef = useRef(null);
  const { addToCart, isInCart } = useCart();
  const { canAccess, membership } = useMembership();
  const { isAdmin, isAuthenticated, user } = useAuth();
  const { enabled: editEnabled } = usePageEditMode();
  const inCart = isInCart(course.id);
  const p = `video.${course.id}`;
  
  // Admin users have full access to all content
  // Check multiple sources for admin authentication
  const legacyAdminAuth = sessionStorage.getItem('admin_auth');
  const authToken = localStorage.getItem('auth_token');
  const authUserStr = localStorage.getItem('auth_user');
  
  // Parse auth user safely
  let isAdminFromStorage = false;
  try {
    if (authUserStr) {
      const authUser = JSON.parse(authUserStr);
      isAdminFromStorage = authUser?.role === 'admin';
    }
  } catch (e) {
    console.log('Error parsing auth_user');
  }
  
  // Check from AuthContext
  const isAdminFromContext = isAuthenticated && isAdmin && typeof isAdmin === 'function' && isAdmin();
  
  // Check from user object directly
  const isAdminFromUser = user?.role === 'admin';
  
  // User is admin if ANY of these are true
  const userIsAdmin = isAdminFromContext || isAdminFromUser || !!legacyAdminAuth || isAdminFromStorage;
  
  // Check content tier
  const isTrailer = course.is_trailer || course.title?.toLowerCase().includes('trailer');
  const isExclusive = course.is_exclusive || course.title?.toLowerCase().includes('exclusive');
  
  // Determine required tier
  let requiredTier = 'free';
  if (isExclusive) {
    requiredTier = 'gold';
  } else if (!isTrailer) {
    requiredTier = 'watch-all';
  }
  
  // Admin bypass - admins can always access content
  const isLocked = userIsAdmin ? false : !canAccess(requiredTier);

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes} min`;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      instructional: 'Instructional',
      performance: 'Performance',
      documentary: 'Documentary',
      general: 'General'
    };
    return labels[category] || 'General';
  };

  const getCategoryColor = (category) => {
    const colors = {
      instructional: 'bg-blue-500/20 text-blue-400',
      performance: 'bg-purple-500/20 text-purple-400',
      documentary: 'bg-green-500/20 text-green-400',
      general: 'bg-amber-500/20 text-amber-400'
    };
    return colors[category] || colors.general;
  };

  const handlePlayTrailer = () => {
    // Check if locked
    if (isLocked && !isTrailer) {
      setShowUpgradeModal(true);
      return;
    }
    
    if (course.trailer_url) {
      setShowTrailer(true);
      
      // If it's a direct video file, play it
      const embedUrl = getVideoEmbedUrl(course.trailer_url);
      if (!embedUrl) {
        setTimeout(() => {
          if (videoRef.current) {
            videoRef.current.play();
            setIsPlaying(true);
          }
        }, 100);
      }
    }
  };

  const handleTogglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const embedUrl = course.trailer_url ? getVideoEmbedUrl(course.trailer_url) : null;

  const handleAddToCart = async () => {
    if (inCart) {
      toast.info('Already in cart');
      return;
    }
    try {
      await addToCart('video_course', course.id);
      toast.success('Course added to cart!');
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="group relative bg-card border border-border/50 rounded-2xl overflow-hidden hover:border-primary/50 transition-all"
      data-testid={`video-course-card-${course.id}`}
    >
      {editEnabled && onDelete && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(course); }}
          data-edit-safe
          data-testid={`delete-video-${course.id}`}
          className="absolute top-3 right-3 z-30 p-2 rounded-full bg-red-500/90 hover:bg-red-500 text-white shadow-lg"
          title="Delete this video course"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
      {/* Thumbnail / Trailer */}
      <div className="relative aspect-video bg-stone-900">
        {showTrailer && course.trailer_url ? (
          embedUrl ? (
            // Vimeo/YouTube embed with official parameters
            <iframe
              src={embedUrl}
              className="w-full h-full"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          ) : (
            // Direct video file
            <video
              ref={videoRef}
              src={course.trailer_url}
              className="w-full h-full object-cover"
              onEnded={() => {
                setIsPlaying(false);
                setShowTrailer(false);
              }}
              onClick={handleTogglePlay}
            />
          )
        ) : (
          <img
            src={course.thumbnail_url || 'https://images.unsplash.com/photo-1516280440614-37939bbacd81?w=800'}
            alt={course.title}
            className="w-full h-full object-cover"
            style={course.thumbnail_position ? { objectPosition: course.thumbnail_position } : undefined}
          />
        )}

        {/* Play Overlay */}
        {!showTrailer && course.trailer_url && (
          <button
            onClick={handlePlayTrailer}
            className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <div className="w-16 h-16 rounded-full bg-primary/90 flex items-center justify-center hover:scale-110 transition-transform">
              <Play className="w-8 h-8 text-primary-foreground ml-1" fill="currentColor" />
            </div>
          </button>
        )}

        {/* Playing Controls - only for direct video */}
        {showTrailer && !embedUrl && (
          <button
            onClick={handleTogglePlay}
            className="absolute bottom-4 left-4 p-2 bg-black/60 rounded-full"
          >
            {isPlaying ? (
              <Pause className="w-5 h-5 text-white" />
            ) : (
              <Play className="w-5 h-5 text-white" fill="currentColor" />
            )}
          </button>
        )}

        {/* Close trailer button for embeds */}
        {showTrailer && embedUrl && (
          <button
            onClick={() => setShowTrailer(false)}
            className="absolute top-3 left-3 px-3 py-1 bg-black/60 rounded-full text-xs font-bold text-white hover:bg-black/80"
          >
            <PageEditable path="labels.closePreview" as="span" fallback="Close Preview" />
          </button>
        )}

        {/* Category Badge */}
        <div className="absolute top-3 left-3 flex gap-2">
          <span className={`px-3 py-1 rounded-full text-xs font-bold ${getCategoryColor(course.category)}`}>
            <PageEditable path={`${p}.categoryLabel`} as="span" fallback={getCategoryLabel(course.category)} />
          </span>
          
          {/* Exclusive Gold Badge */}
          {isExclusive && (
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-amber-500 to-yellow-400 text-black flex items-center gap-1">
              <Crown className="w-3 h-3" />
              <PageEditable path="labels.goldExclusive" as="span" fallback="Gold Exclusive" />
            </span>
          )}
        </div>

        {/* Video Count Badge */}
        <div className="absolute top-3 right-3">
          <span className="px-3 py-1 bg-black/60 rounded-full text-xs font-bold text-white flex items-center gap-1">
            <Video className="w-3 h-3" />
            {course.video_count || course.video_ids?.length || 0} <PageEditable path="labels.videosSuffix" as="span" fallback="videos" />
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="text-lg font-bold font-display text-foreground mb-2 line-clamp-2">
          <PageEditable path={`${p}.title`} as="span" fallback={course.title || ''} />
        </h3>
        
        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
          <PageEditable path={`${p}.description`} as="span" multiline fallback={course.description || `By ${course.instructor}`} />
        </p>

        {/* Meta */}
        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
          <span className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            {formatDuration(course.total_duration || 0)}
          </span>
          <span>
            <PageEditable path={`${p}.instructor`} as="span" fallback={course.instructor || ''} />
          </span>
        </div>

        {/* Price and Add to Cart */}
        <div className="flex items-center justify-between">
          <span className="text-2xl font-bold font-display text-primary">
            $<PageEditable path={`${p}.price`} as="span" fallback={course.price ? course.price.toFixed(2) : '0.00'} />
          </span>
          <button
            onClick={handleAddToCart}
            disabled={inCart}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-bold transition-all ${
              inCart
                ? 'bg-green-500/20 text-green-400 cursor-default'
                : 'bg-primary hover:bg-primary/90 text-primary-foreground hover:scale-105'
            }`}
            data-testid={`add-course-${course.id}`}
          >
            {inCart ? (
              <>
                <Check className="w-4 h-4" />
                <PageEditable path="labels.inCart" as="span" fallback="In Cart" />
              </>
            ) : (
              <>
                <ShoppingCart className="w-4 h-4" />
                <PageEditable path="labels.addToCart" as="span" fallback="Add to Cart" />
              </>
            )}
          </button>
        </div>
      </div>
      
      {/* Locked Overlay */}
      {isLocked && !isTrailer && (
        <div 
          onClick={() => setShowUpgradeModal(true)}
          className="absolute inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center cursor-pointer group rounded-2xl z-10"
        >
          <div className={`w-16 h-16 ${isExclusive ? 'bg-gradient-to-r from-amber-500 to-yellow-400' : 'bg-blue-500'} rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
            {isExclusive ? <Crown className="w-8 h-8 text-black" /> : <Lock className="w-8 h-8 text-white" />}
          </div>
          <p className="text-white font-bold text-lg">
            {isExclusive
              ? <PageEditable path="lockedOverlay.exclusiveTitle" as="span" fallback="Gold Exclusive" />
              : <PageEditable path="lockedOverlay.premiumTitle" as="span" fallback="Premium Content" />}
          </p>
          <p className="text-white/70 text-sm">
            <PageEditable path="lockedOverlay.requiresPrefix" as="span" fallback="Requires" /> {isExclusive ? 'Gold' : 'Watch All'} <PageEditable path="lockedOverlay.requiresSuffix" as="span" fallback="membership" />
          </p>
          <button className={`mt-4 px-6 py-2 ${isExclusive ? 'bg-gradient-to-r from-amber-500 to-yellow-400 text-black' : 'bg-blue-500 text-white'} rounded-full text-sm font-semibold hover:opacity-90`}>
            <PageEditable path="lockedOverlay.unlockBtn" as="span" fallback="Unlock Now" />
          </button>
        </div>
      )}
      
      {/* Upgrade Modal */}
      <UpgradeModal 
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        requiredTier={requiredTier}
        contentTitle={course.title}
      />
    </motion.div>
  );
};
